import { ChatFileDownloadStatus, ChatFileUploadStatus } from "@zoom/videosdk";
import { useCallback, useEffect } from "react";


const useHandleZoomEvents = ({
  zmClient,
  apellidoNombrePaciente,
  apellidoNombreProfesional,
  esPaciente,
  zoomChat,
  isChatOpenRef,
  setParticipants,
  setUnreadMessages,
  setMessages,
  setUploadProgress
}) => {

  const refreshParticipants = useCallback(() => {
    setParticipants(zmClient.getAllUser());
  }, [zmClient, setParticipants]);

  const handleChatMessage = useCallback((payload) => {
    // Se asume que payload trae: id, timestamp, sender, message y file (si lo hay)
    const { id, sender, message, file } = payload;

    const etiquetaNombre =
      sender.name === "Paciente"
        ? apellidoNombrePaciente
        : apellidoNombreProfesional;
    if (file?.fileUrl && !esPaciente) {
      zoomChat.downloadFile(id, file.fileUrl, true);
    }

    const currentUserId = zmClient.getCurrentUserInfo()?.userId;
    const isFromOtherUser = sender.userId !== currentUserId;
    if (isFromOtherUser && !isChatOpenRef.current) {
      setUnreadMessages((prev) => prev + 1);
    }

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: prevMessages.length + 1,
        zoomId: id,
        senderName: etiquetaNombre,
        senderRol: sender.name,
        senderId: sender.userId,
        mensaje: message,
        archivo: file ? {
          ...file,
          nombreArchivo: file.name,
          archivo: null,
        } : null,

        fechaHoraEnvio: new Date().toISOString(),
      },
    ]);
  }, [apellidoNombrePaciente, apellidoNombreProfesional, zmClient, zoomChat, esPaciente, isChatOpenRef, setMessages, setUnreadMessages]);

  const handleFileDownload = useCallback((payload) => {
    const { fileName, status, fileBlob, fileUrl } = payload;
    if (status === ChatFileDownloadStatus.Success) {
      if (fileBlob) {
        try {
          if (!esPaciente) {
            const reader = new FileReader();

            reader.onloadend = () => {
              const base64DataUrl = reader.result;
              const base64String = base64DataUrl.split(',')[1];

              setMessages((prevMessages) => {
                return prevMessages.map((msg) => {
                  if (msg.archivo
                    && msg.archivo.nombreArchivo === fileName
                    && msg.archivo.fileUrl === fileUrl
                  ) {
                    return {
                      ...msg,
                      archivo: {
                        ...msg.archivo,
                        archivo: base64String,
                      }
                    };
                  }
                  return msg;
                });
              });
            };
            // Trigger the read
            reader.readAsDataURL(fileBlob);
          }
        } catch (err) {
          console.error('Error converting blob to base64:', err);
        }
      }
    }
  }, [esPaciente, setMessages]);

  const handleFileUploadProgress = useCallback(({ progress, status }) => {
    if (status === ChatFileUploadStatus.InProgress) {
      setUploadProgress(true);
    }
    if (status === ChatFileUploadStatus.Success || status === ChatFileUploadStatus.Failed) {
      setUploadProgress(false);
    }
  }, [setUploadProgress]);



  useEffect(() => {
    zmClient.on("chat-on-message", handleChatMessage);
    zmClient.on("chat-file-upload-progress", handleFileUploadProgress);
    zmClient.on("user-added", refreshParticipants);
    zmClient.on("chat-file-download-progress", handleFileDownload);
    return () => {
      zmClient.off("chat-on-message", handleChatMessage);
      zmClient.off("chat-file-upload-progress", handleFileUploadProgress);
      zmClient.off("user-added", refreshParticipants);
      zmClient.off("chat-file-download-progress", handleFileDownload);
    };
  }, [zmClient, handleFileUploadProgress, refreshParticipants, handleChatMessage, handleFileDownload]);
  return;
}

export default useHandleZoomEvents;